"use client";

import { EMAIL_FORWARDING_DOMAIN } from "@repo/api/services/email-forwarding/constants";
import { COMPANY_EMAILS } from "@repo/common";
import { Alert, AlertDescription, AlertTitle } from "@repo/ui";
import { AlertTriangle, ShieldBan } from "lucide-react";
import { FaLinkedinIn } from "react-icons/fa";

import {
  setSignedUpViaJoinCookie,
  setSignupSourceCookie,
} from "@/app/actions/signup-source";
import {
  FacebookIcon,
  GitHubIcon,
  GoogleIcon,
  LinkedInIcon,
  MicrosoftIcon,
} from "@/components/app/brand-icons";
import { AUDIENCE_HERO } from "@/components/auth/audience-hero";
import {
  type AuthProvider,
  OAuthSignIn,
  type OAuthSignInConfig,
} from "@/components/auth/oauth-sign-in";
import { persistPostHogSessionForSignup } from "@/lib/posthog-signup-session";
import { inferSignupIntent, setSignupIntent } from "@/lib/signup-intent";

export type ExpertSignInMode = "login" | "join";

interface ExpertSignInProps {
  mode: ExpertSignInMode;
  returnUrl?: string;
  redirectTo?: string;
  onboardingInterviewId?: string;
  isBanned?: boolean;
  isExpertEmail?: boolean;
  /** Attributes the signup to `referral_link` instead of `login_page`. */
  isReferral?: boolean;
  /**
   * Set when the login was initiated by an OAuth 2.1 authorize request. After
   * sign-in the user is sent back to this signed /oauth2/authorize URL so the
   * provider resumes the flow and issues a code to the client.
   */
  oauthContinueUrl?: string;
  /** Adds the magic-link email form to login. */
  showMagicLink?: boolean;
}

const SECONDARIES: AuthProvider[] = [
  {
    id: "facebook",
    label: "Facebook",
    icon: <FacebookIcon className="size-[27px]" />,
  },
  {
    id: "google",
    label: "Google",
    icon: <GoogleIcon className="size-[27px]" />,
  },
  {
    id: "microsoft",
    label: "Microsoft",
    icon: <MicrosoftIcon className="size-[27px]" />,
  },
  {
    id: "github",
    label: "GitHub",
    icon: <GitHubIcon className="text-foreground size-[27px]" />,
  },
];

export function ExpertSignIn({
  mode,
  returnUrl,
  redirectTo,
  onboardingInterviewId,
  isBanned,
  isExpertEmail,
  oauthContinueUrl,
  showMagicLink,
  isReferral,
}: ExpertSignInProps) {
  const isLogin = mode === "login";

  const finalRedirect = isLogin
    ? oauthContinueUrl || returnUrl || redirectTo || "/me"
    : returnUrl
      ? `/join?returnUrl=${encodeURIComponent(returnUrl)}`
      : "/join";

  const topSlot =
    isLogin && (isBanned || isExpertEmail) ? (
      <>
        {isBanned && (
          <Alert variant="destructive" className="mb-6">
            <ShieldBan className="h-4 w-4" />
            <AlertTitle>Account Banned</AlertTitle>
            <AlertDescription>
              Your account has been banned. If you believe this is a mistake,
              please contact{" "}
              <a
                href={`mailto:${COMPANY_EMAILS.support}`}
                className="font-medium underline"
              >
                {COMPANY_EMAILS.support}
              </a>{" "}
              for assistance.
            </AlertDescription>
          </Alert>
        )}
        {isExpertEmail && (
          <Alert className="mb-6 border-amber-500/50 bg-amber-500/10 text-amber-800 dark:border-amber-500/40 dark:text-amber-300 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Use your personal email</AlertTitle>
            <AlertDescription>
              @{EMAIL_FORWARDING_DOMAIN} addresses can&apos;t be used to sign in
              to Terac. Please continue with your personal email instead.
            </AlertDescription>
          </Alert>
        )}
      </>
    ) : undefined;

  const config: OAuthSignInConfig = {
    // Same panel step 1 previewed when this side was picked, so the two steps
    // read as one flow rather than two screens.
    heroAudience: "expert",
    heroHeadline: AUDIENCE_HERO.expert.headline,
    heroSubtitle: AUDIENCE_HERO.expert.subtitle,
    heroPanelClass: AUDIENCE_HERO.expert.panelClass,
    // Names the audience, so someone who came through the gate can see at a
    // glance that they landed on the side they picked. /join is not reached
    // through the gate, so it says "Sign up" instead.
    heading: isLogin ? "Sign in as an Expert" : "Sign up as an Expert",
    subheading: isLogin ? undefined : "Login or sign up to get started.",
    // The mirror of the expert link on /researchers/login, so the two routes
    // reach each other in both directions now that the gate is gone.
    crossLink: isLogin
      ? {
          href: "/researchers/login",
          prompt: "Are you a researcher?",
          label: "Start your research",
        }
      : undefined,
    topSlot,
    primary: {
      id: "linkedin",
      label: "LinkedIn",
      icon: <LinkedInIcon className="size-[27px]" />,
      buttonIcon: <FaLinkedinIn className="h-[17px] w-[17px] text-white" />,
    },
    secondaries: isLogin ? SECONDARIES : undefined,
    magicLink: showMagicLink ? {} : undefined,
    finalRedirect,
    onBeforeSignIn: async () => {
      if (isLogin) {
        await setSignupSourceCookie(
          isReferral ? "referral_link" : "login_page",
        );
        setSignupIntent(
          inferSignupIntent({
            returnUrl,
            redirectTo,
            fromInterviewPage: Boolean(onboardingInterviewId),
          }),
        );
      } else {
        await setSignupSourceCookie("join_page_split");
        await setSignedUpViaJoinCookie();
        // Relay the PostHog session id so signup_completed links to this
        // recording (see persistPostHogSessionForSignup).
        await persistPostHogSessionForSignup();
        setSignupIntent("participant");
      }
    },
  };

  return <OAuthSignIn config={config} />;
}
